import { useState, useEffect } from "react";
import { Link, useNavigate, Outlet, useParams, NavLink } from "react-router-dom";
import { opsApi, useOpsAuth } from "./OpsAuth";
import { Warehouse, LogOut, Home, Inbox, Boxes, PackageCheck, Loader2, CheckCircle2, ArrowLeft, PlusCircle, Truck } from "lucide-react";

// ═══════════════ LOGIN ═══════════════
export const OpsLogin = () => {
  const { login } = useOpsAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email, pw); nav("/ops", { replace: true }); }
    catch (e) { setErr(e?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-slate-950 grid place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-emerald-500/20 text-emerald-400 mb-4">
            <Warehouse className="h-7 w-7" />
          </div>
          <h1 className="font-display font-black text-white text-3xl tracking-tight">Warehouse Ops</h1>
          <p className="text-slate-400 text-sm mt-1">Turbo Transit fulfillment center</p>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-slate-800 bg-slate-900 p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Email</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="ops-login-email" className="w-full rounded-xl bg-slate-800 border border-slate-700 text-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 transition" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Password</label>
            <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" data-testid="ops-login-password" className="w-full rounded-xl bg-slate-800 border border-slate-700 text-white px-4 py-2.5 text-sm outline-none focus:border-emerald-500 transition" />
          </div>
          {err && <div className="text-sm text-red-400" data-testid="ops-login-error">{err}</div>}
          <button type="submit" disabled={busy} data-testid="ops-login-submit" className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm py-3 transition">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ═══════════════ LAYOUT ═══════════════
const NavLinkBtn = ({ to, icon: Icon, children, end, testid }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition ${
        isActive ? "bg-emerald-500 text-slate-950" : "text-slate-300 hover:bg-slate-800 hover:text-white"
      }`
    }
  >
    <Icon className="h-4 w-4" /> {children}
  </NavLink>
);

export const OpsLayout = () => {
  const { user, logout } = useOpsAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <header className="border-b border-slate-800 bg-slate-900/70 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <Link to="/ops" className="flex items-center gap-2">
            <div className="grid place-items-center h-8 w-8 rounded-lg bg-emerald-500/20 text-emerald-400">
              <Warehouse className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display font-black text-white text-sm">Warehouse Ops</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Turbo Transit</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <div className="text-xs text-slate-400 hidden sm:block">{user?.email}</div>
            <button onClick={async () => { await logout(); nav("/ops/login"); }} data-testid="ops-logout" className="text-xs text-slate-400 hover:text-red-400 inline-flex items-center gap-1">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-6 py-8 grid md:grid-cols-[220px_1fr] gap-8">
        <aside className="space-y-1">
          <NavLinkBtn to="/ops" end icon={Home} testid="ops-nav-dashboard">Dashboard</NavLinkBtn>
          <NavLinkBtn to="/ops/receiving" icon={Inbox} testid="ops-nav-receiving">Receiving</NavLinkBtn>
          <NavLinkBtn to="/ops/bins" icon={Boxes} testid="ops-nav-bins">Bins</NavLinkBtn>
          <NavLinkBtn to="/ops/orders" icon={PackageCheck} testid="ops-nav-orders">Orders</NavLinkBtn>
        </aside>
        <main><Outlet /></main>
      </div>
    </div>
  );
};

// ═══════════════ DASHBOARD ═══════════════
const StatTile = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
    <div className="font-display font-black text-4xl text-white mt-2">{value}</div>
  </div>
);

export const OpsDashboard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { opsApi.stats().then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-slate-400 text-sm">Loading…</div>;
  return (
    <div>
      <h1 className="font-display font-black text-white text-3xl tracking-tight mb-1">Dashboard</h1>
      <p className="text-slate-400 text-sm mb-6">Live warehouse status</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Pending ASNs" value={s.pending_asns} testid="ops-stat-asns" />
        <StatTile label="New orders" value={s.new_orders} testid="ops-stat-new-orders" />
        <StatTile label="Picked / Packed" value={`${s.picked} / ${s.packed}`} testid="ops-stat-picked-packed" />
        <StatTile label="Shipped today" value={s.shipped_today} testid="ops-stat-shipped" />
        <StatTile label="Total bins" value={s.total_bins} testid="ops-stat-bins" />
        <StatTile label="Total shippers" value={s.total_shippers} testid="ops-stat-shippers" />
      </div>
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <Link to="/ops/receiving" className="rounded-2xl border border-slate-800 bg-slate-900 hover:border-emerald-500 transition p-5">
          <Inbox className="h-6 w-6 text-emerald-400 mb-3" />
          <div className="font-semibold text-white">Receive shipments</div>
          <div className="text-xs text-slate-400 mt-1">Log incoming ASNs into bins.</div>
        </Link>
        <Link to="/ops/orders?status=new" className="rounded-2xl border border-slate-800 bg-slate-900 hover:border-emerald-500 transition p-5">
          <PackageCheck className="h-6 w-6 text-emerald-400 mb-3" />
          <div className="font-semibold text-white">Fulfill orders</div>
          <div className="text-xs text-slate-400 mt-1">Pick, pack, and ship open orders.</div>
        </Link>
        <Link to="/ops/bins" className="rounded-2xl border border-slate-800 bg-slate-900 hover:border-emerald-500 transition p-5">
          <Boxes className="h-6 w-6 text-emerald-400 mb-3" />
          <div className="font-semibold text-white">Bins & inventory</div>
          <div className="text-xs text-slate-400 mt-1">View stock by bin location.</div>
        </Link>
      </div>
    </div>
  );
};

// ═══════════════ RECEIVING ═══════════════
const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-500/20 text-amber-300",
    partial: "bg-blue-500/20 text-blue-300",
    received: "bg-emerald-500/20 text-emerald-300",
    new: "bg-slate-500/20 text-slate-300",
    picking: "bg-blue-500/20 text-blue-300",
    picked: "bg-indigo-500/20 text-indigo-300",
    packed: "bg-violet-500/20 text-violet-300",
    shipped: "bg-emerald-500/20 text-emerald-300",
    backorder: "bg-red-500/20 text-red-300",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${map[status] || map.pending}`}>{status}</span>;
};

export const OpsReceiving = () => {
  const [asns, setAsns] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [items, setItems] = useState({}); // { sku: {bin, qty} }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => opsApi.receiving().then((r) => setAsns(r.data.asns || []));
  useEffect(() => { load(); }, []);

  const openReceive = (asn) => {
    setExpanded(asn.id);
    const init = {};
    (asn.items || []).forEach((it) => { init[it.sku] = { bin: "A-01", qty: it.qty }; });
    setItems(init);
    setErr("");
  };

  const submit = async (asn_id) => {
    setBusy(true); setErr("");
    try {
      const payload = Object.entries(items)
        .filter(([, v]) => Number(v.qty) > 0 && v.bin)
        .map(([sku, v]) => ({ sku, bin_code: v.bin.toUpperCase(), qty: Number(v.qty) }));
      if (!payload.length) throw new Error("Enter at least one row");
      await opsApi.receive(asn_id, payload);
      setExpanded(null);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div>
      <h1 className="font-display font-black text-white text-3xl tracking-tight mb-1">Receiving queue</h1>
      <p className="text-slate-400 text-sm mb-6">Log incoming inventory into bins.</p>
      {asns.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900 p-10 text-center text-slate-400 text-sm" data-testid="ops-receiving-empty">
          No pending ASNs. When shippers submit ASNs, they'll appear here.
        </div>
      )}
      <div className="space-y-3">
        {asns.map((a) => (
          <div key={a.id} className="rounded-2xl border border-slate-800 bg-slate-900" data-testid={`ops-asn-${a.id}`}>
            <div className="p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold text-white">{a.reference} <span className="text-xs text-slate-400 font-normal">· {a.shipper_name}</span></div>
                <div className="text-xs text-slate-400 mt-0.5">{a.total_units} units expected · {a.items?.length || 0} SKUs</div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={a.status} />
                <button onClick={() => (expanded === a.id ? setExpanded(null) : openReceive(a))} data-testid={`ops-receive-btn-${a.id}`} className="rounded-full bg-emerald-500 text-slate-950 font-bold text-xs px-4 py-2 hover:bg-emerald-400 transition">
                  {expanded === a.id ? "Close" : "Receive"}
                </button>
              </div>
            </div>
            {expanded === a.id && (
              <div className="border-t border-slate-800 p-4 space-y-3">
                {(a.items || []).map((it) => (
                  <div key={it.sku} className="grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-4">
                      <div className="text-xs text-slate-400 uppercase tracking-widest">SKU</div>
                      <div className="font-mono text-white text-sm">{it.sku}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{it.name || "—"}</div>
                    </div>
                    <div className="col-span-4">
                      <div className="text-xs text-slate-400 uppercase tracking-widest">Bin</div>
                      <input value={items[it.sku]?.bin || ""} onChange={(e) => setItems({ ...items, [it.sku]: { ...items[it.sku], bin: e.target.value } })} data-testid={`ops-receive-bin-${it.sku}`} className="mt-0.5 w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-3 py-1.5 text-sm font-mono outline-none focus:border-emerald-500" placeholder="A-01" />
                    </div>
                    <div className="col-span-3">
                      <div className="text-xs text-slate-400 uppercase tracking-widest">Qty received</div>
                      <input type="number" min="0" value={items[it.sku]?.qty ?? 0} onChange={(e) => setItems({ ...items, [it.sku]: { ...items[it.sku], qty: e.target.value } })} data-testid={`ops-receive-qty-${it.sku}`} className="mt-0.5 w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-3 py-1.5 text-sm outline-none focus:border-emerald-500" />
                    </div>
                    <div className="col-span-1 text-xs text-slate-500">/ {it.qty}</div>
                  </div>
                ))}
                {err && <div className="text-sm text-red-400">{err}</div>}
                <div className="flex justify-end">
                  <button onClick={() => submit(a.id)} disabled={busy} data-testid={`ops-receive-submit-${a.id}`} className="rounded-full bg-emerald-500 text-slate-950 font-bold text-sm px-5 py-2 hover:bg-emerald-400 transition disabled:opacity-50">
                    {busy ? "Saving…" : "Confirm receipt"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════ BINS ═══════════════
export const OpsBins = () => {
  const [bins, setBins] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [code, setCode] = useState("");
  const [loc, setLoc] = useState("");
  const [err, setErr] = useState("");
  const load = () => opsApi.listBins().then((r) => setBins(r.data.bins || []));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setErr("");
    try {
      await opsApi.createBin({ code, location: loc || undefined });
      setCode(""); setLoc(""); setShowNew(false);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed");
    }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-black text-white text-3xl tracking-tight">Bins</h1>
          <p className="text-slate-400 text-sm mt-1">Warehouse storage locations.</p>
        </div>
        <button onClick={() => setShowNew(!showNew)} data-testid="ops-bin-new-btn" className="rounded-full bg-emerald-500 text-slate-950 font-bold text-xs px-4 py-2 hover:bg-emerald-400 transition inline-flex items-center gap-1.5">
          <PlusCircle className="h-3.5 w-3.5" /> New bin
        </button>
      </div>
      {showNew && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4 mb-4 flex gap-3 items-end" data-testid="ops-bin-form">
          <div className="flex-1">
            <div className="text-xs text-slate-400 uppercase tracking-widest mb-1">Code</div>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="D-01" data-testid="ops-bin-code" className="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-3 py-2 text-sm font-mono outline-none focus:border-emerald-500" />
          </div>
          <div className="flex-1">
            <div className="text-xs text-slate-400 uppercase tracking-widest mb-1">Location (optional)</div>
            <input value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Aisle D, Slot 1" data-testid="ops-bin-loc" className="w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-3 py-2 text-sm outline-none focus:border-emerald-500" />
          </div>
          <button onClick={create} data-testid="ops-bin-create" className="rounded-full bg-emerald-500 text-slate-950 font-bold text-sm px-5 py-2 hover:bg-emerald-400">Create</button>
          {err && <div className="text-xs text-red-400">{err}</div>}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ops-bin-grid">
        {bins.map((b) => (
          <div key={b.id} className="rounded-2xl border border-slate-800 bg-slate-900 p-4 hover:border-emerald-500 transition" data-testid={`ops-bin-${b.code}`}>
            <div className="font-mono text-emerald-400 text-lg font-bold">{b.code}</div>
            <div className="text-xs text-slate-400 mt-0.5">{b.location || "—"}</div>
            <div className="mt-3 flex items-center justify-between text-xs">
              <div className="text-slate-500">{b.sku_count} SKUs</div>
              <div className="font-bold text-white">{b.total_qty} units</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════ ORDERS QUEUE ═══════════════
export const OpsOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => opsApi.listOrders(filter || null).then((r) => setOrders(r.data.orders || []));
  useEffect(() => { load(); }, [filter]);
  const stages = ["", "new", "picked", "packed", "shipped", "backorder"];
  return (
    <div>
      <h1 className="font-display font-black text-white text-3xl tracking-tight mb-1">Fulfillment queue</h1>
      <p className="text-slate-400 text-sm mb-5">Pick, pack, and ship orders.</p>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="ops-order-filters">
        {stages.map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`ops-filter-${s || "all"}`} className={`text-xs font-semibold px-3.5 py-2 rounded-full transition border ${filter === s ? "bg-emerald-500 text-slate-950 border-emerald-500" : "bg-slate-900 text-slate-300 border-slate-800 hover:border-emerald-500"}`}>
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>
      {orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-800 bg-slate-900 p-10 text-center text-slate-400 text-sm" data-testid="ops-orders-empty">
          No orders match this filter.
        </div>
      ) : (
        <div className="space-y-2" data-testid="ops-orders-list">
          {orders.map((o) => (
            <Link to={`/ops/orders/${o.id}`} key={o.id} data-testid={`ops-order-${o.id}`} className="block rounded-2xl border border-slate-800 bg-slate-900 hover:border-emerald-500 transition p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-white">
                    {o.shopify_order_number ? `#${o.shopify_order_number}` : o.id.slice(0, 8)}
                    <span className="text-xs text-slate-400 font-normal ml-2">· {o.shipper_name || "—"}</span>
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {o.recipient?.name || "—"} · {o.recipient?.city || ""} {o.recipient?.state || ""} {o.recipient?.country || ""} · {o.items?.length || 0} items
                  </div>
                </div>
                <StatusBadge status={o.status} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════ ORDER DETAIL / FULFILL ═══════════════
export const OpsOrderDetail = () => {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("");
  const load = () => opsApi.picklist(orderId).then((r) => setData(r.data));
  useEffect(() => { load(); }, [orderId]);
  const act = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!data) return <div className="text-slate-400 text-sm">Loading…</div>;
  const { order, picks } = data;
  return (
    <div>
      <button onClick={() => nav(-1)} className="text-slate-400 hover:text-white text-sm inline-flex items-center gap-1 mb-4" data-testid="ops-order-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-black text-white text-3xl tracking-tight">
            {order.shopify_order_number ? `Order #${order.shopify_order_number}` : `Order ${order.id.slice(0, 8)}`}
          </h1>
          <p className="text-slate-400 text-sm mt-1">Ship to {order.recipient?.name}</p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">Ship to</div>
          <div className="text-white font-semibold">{order.recipient?.name}</div>
          <div className="text-slate-300 text-sm mt-1">
            {order.recipient?.street1}<br />
            {order.recipient?.street2 ? <>{order.recipient.street2}<br /></> : null}
            {order.recipient?.city}, {order.recipient?.state} {order.recipient?.zip}<br />
            {order.recipient?.country}
          </div>
          {order.recipient?.phone && <div className="text-xs text-slate-400 mt-2">☎ {order.recipient.phone}</div>}
          {order.recipient?.email && <div className="text-xs text-slate-400">✉ {order.recipient.email}</div>}
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">Pick list</div>
          <div className="space-y-3" data-testid="ops-picklist">
            {picks.map((p, i) => (
              <div key={i} className="border-b border-slate-800 pb-3 last:border-0">
                <div className="flex justify-between text-sm">
                  <div>
                    <div className="font-mono text-white">{p.sku}</div>
                    <div className="text-xs text-slate-400">{p.name || "—"}</div>
                  </div>
                  <div className="text-emerald-400 font-bold">×{p.qty_needed}</div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.locations.length === 0 && <span className="text-xs text-red-400">No stock — will backorder</span>}
                  {p.locations.map((l, li) => (
                    <span key={li} className="text-[10px] font-mono bg-slate-800 text-emerald-300 px-2 py-0.5 rounded">
                      {l.bin_code}: {l.qty_available}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {err && <div className="mt-4 text-sm text-red-400" data-testid="ops-order-err">{err}</div>}

      <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900 p-5">
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">Actions</div>
        <div className="flex flex-wrap gap-2 items-center">
          <button disabled={busy || !["new", "picking"].includes(order.status)} onClick={() => act(() => opsApi.pick(orderId))} data-testid="ops-btn-pick" className="rounded-full bg-emerald-500 text-slate-950 font-bold text-xs px-4 py-2 hover:bg-emerald-400 transition disabled:opacity-40 disabled:cursor-not-allowed">
            Mark Picked
          </button>
          <button disabled={busy || order.status !== "picked"} onClick={() => act(() => opsApi.pack(orderId))} data-testid="ops-btn-pack" className="rounded-full bg-emerald-500 text-slate-950 font-bold text-xs px-4 py-2 hover:bg-emerald-400 transition disabled:opacity-40 disabled:cursor-not-allowed">
            Mark Packed
          </button>
          <div className="h-6 w-px bg-slate-700 mx-1" />
          <input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Carrier" data-testid="ops-ship-carrier" className="rounded-lg bg-slate-800 border border-slate-700 text-white px-3 py-1.5 text-sm outline-none focus:border-emerald-500 w-32" />
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Tracking #" data-testid="ops-ship-tracking" className="rounded-lg bg-slate-800 border border-slate-700 text-white px-3 py-1.5 text-sm outline-none focus:border-emerald-500 w-48" />
          <button disabled={busy || order.status !== "packed"} onClick={() => act(() => opsApi.ship(orderId, { tracking_code: tracking, carrier }))} data-testid="ops-btn-ship" className="rounded-full bg-emerald-500 text-slate-950 font-bold text-xs px-4 py-2 hover:bg-emerald-400 transition disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1">
            <Truck className="h-3.5 w-3.5" /> Mark Shipped
          </button>
        </div>
        {order.tracking_code && (
          <div className="mt-3 text-sm text-emerald-300 inline-flex items-center gap-1.5" data-testid="ops-tracking-display">
            <CheckCircle2 className="h-4 w-4" /> Shipped via {order.carrier || "carrier"} — <span className="font-mono">{order.tracking_code}</span>
          </div>
        )}
      </div>
    </div>
  );
};
